import "./dialog.css"

import { Util } from "../../../utils/util";

export interface IDialogButton {
    label: string,
    value: string,
    isSecondary?: boolean,
    action?: () => void
}

/**
 * Represents a customizable dialog modal.
 * Provides a message and buttons for user interaction.
 */
export class Dialog {
    private readonly _element: HTMLDialogElement;

    /**
     * Creates an instance of a dialog modal.
     * @param {EventEmitter} eventBus - The event bus for handling global events.
     * @param {string} message - The message to display in the dialog.
     * @param {IDialogButton[]} buttons - The buttons to display in the dialog.
     */
    constructor(message: string, buttons: IDialogButton[]) {
        this._element = document.createElement("dialog");
        this._element.classList.add("dialog");
        this._element.id = `dialog_${Util.generateHex(4)}`
        this._element.setAttribute("aria-describedby", `${this._element.id}_message`);
        this._element.addEventListener("close", () => this._handleDialogClose());

        const elementMessage = document.createElement("div");
        elementMessage.id = `${this._element.id}_message`;
        elementMessage.classList.add("dialog-message");
        elementMessage.innerHTML = message;

        const elementButtons = document.createElement("div");
        elementButtons.classList.add("dialog-buttons");

        for (const button of buttons) {
            const elementButton = this._createDialogButton(button);
            elementButtons.append(elementButton);
        }

        this._element.append(elementMessage, elementButtons);
        document.body.appendChild(this._element);
        this._element.showModal();
    }

    /**
     * Creates a button element for the dialog.
     * @param {IDialogButton} buttonData - The button data including label, value, and action.
     * @returns {HTMLButtonElement} The generated button element.
     * @private
     */
    private _createDialogButton(button: IDialogButton): HTMLButtonElement {
        const elementButton = document.createElement("button");
        elementButton.type = "button";
        elementButton.value = button.value;
        elementButton.innerHTML = button.label;

        if (button.isSecondary)
            elementButton.classList.add("secondary");

        elementButton.onclick = () => this._handleButtonClick(button.action);
        return elementButton;
    }

    /**
     * Handles the button click event in the dialog.
     * Executes the button's action if provided and closes the dialog.
     * @param {(() => void) | undefined} action - The optional action to execute on click.
     * @private
     */
    private readonly _handleButtonClick = (action?: () => void): void => {
        action?.();
        this._element.close();
    }

    /**
     * Handles cleanup logic when the dialog is closed, regardless of the trigger
     * (button click, Escape key, or programmatic close).
     *
     * @private
     * @returns {void}
     */
    private readonly _handleDialogClose = (): void => {
        this._element.remove();
    }

    public readonly close = (): void => this._handleDialogClose();
}